import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";

export interface Country {
  name: string;
  code: string; // ISO
  dial: string; // "351"
  flag: string; // emoji
}

// Curated list, includes PT/BR/ES first and most-used worldwide
export const COUNTRIES: Country[] = [
  { name: "Portugal", code: "PT", dial: "351", flag: "🇵🇹" },
  { name: "Brasil", code: "BR", dial: "55", flag: "🇧🇷" },
  { name: "Espanha", code: "ES", dial: "34", flag: "🇪🇸" },
  { name: "França", code: "FR", dial: "33", flag: "🇫🇷" },
  { name: "Reino Unido", code: "GB", dial: "44", flag: "🇬🇧" },
  { name: "Itália", code: "IT", dial: "39", flag: "🇮🇹" },
  { name: "Alemanha", code: "DE", dial: "49", flag: "🇩🇪" },
  { name: "Estados Unidos", code: "US", dial: "1", flag: "🇺🇸" },
  { name: "Canadá", code: "CA", dial: "1", flag: "🇨🇦" },
  { name: "Suíça", code: "CH", dial: "41", flag: "🇨🇭" },
  { name: "Bélgica", code: "BE", dial: "32", flag: "🇧🇪" },
  { name: "Países Baixos", code: "NL", dial: "31", flag: "🇳🇱" },
  { name: "Luxemburgo", code: "LU", dial: "352", flag: "🇱🇺" },
  { name: "Irlanda", code: "IE", dial: "353", flag: "🇮🇪" },
  { name: "Áustria", code: "AT", dial: "43", flag: "🇦🇹" },
  { name: "Suécia", code: "SE", dial: "46", flag: "🇸🇪" },
  { name: "Noruega", code: "NO", dial: "47", flag: "🇳🇴" },
  { name: "Dinamarca", code: "DK", dial: "45", flag: "🇩🇰" },
  { name: "Finlândia", code: "FI", dial: "358", flag: "🇫🇮" },
  { name: "Polónia", code: "PL", dial: "48", flag: "🇵🇱" },
  { name: "Roménia", code: "RO", dial: "40", flag: "🇷🇴" },
  { name: "República Checa", code: "CZ", dial: "420", flag: "🇨🇿" },
  { name: "Grécia", code: "GR", dial: "30", flag: "🇬🇷" },
  { name: "Ucrânia", code: "UA", dial: "380", flag: "🇺🇦" },
  { name: "Rússia", code: "RU", dial: "7", flag: "🇷🇺" },
  { name: "Turquia", code: "TR", dial: "90", flag: "🇹🇷" },
  { name: "Marrocos", code: "MA", dial: "212", flag: "🇲🇦" },
  { name: "Argélia", code: "DZ", dial: "213", flag: "🇩🇿" },
  { name: "Tunísia", code: "TN", dial: "216", flag: "🇹🇳" },
  { name: "Egito", code: "EG", dial: "20", flag: "🇪🇬" },
  { name: "África do Sul", code: "ZA", dial: "27", flag: "🇿🇦" },
  { name: "Angola", code: "AO", dial: "244", flag: "🇦🇴" },
  { name: "Moçambique", code: "MZ", dial: "258", flag: "🇲🇿" },
  { name: "Cabo Verde", code: "CV", dial: "238", flag: "🇨🇻" },
  { name: "Guiné-Bissau", code: "GW", dial: "245", flag: "🇬🇼" },
  { name: "São Tomé e Príncipe", code: "ST", dial: "239", flag: "🇸🇹" },
  { name: "Timor-Leste", code: "TL", dial: "670", flag: "🇹🇱" },
  { name: "Argentina", code: "AR", dial: "54", flag: "🇦🇷" },
  { name: "México", code: "MX", dial: "52", flag: "🇲🇽" },
  { name: "Chile", code: "CL", dial: "56", flag: "🇨🇱" },
  { name: "Colômbia", code: "CO", dial: "57", flag: "🇨🇴" },
  { name: "Peru", code: "PE", dial: "51", flag: "🇵🇪" },
  { name: "Venezuela", code: "VE", dial: "58", flag: "🇻🇪" },
  { name: "Uruguai", code: "UY", dial: "598", flag: "🇺🇾" },
  { name: "Paraguai", code: "PY", dial: "595", flag: "🇵🇾" },
  { name: "China", code: "CN", dial: "86", flag: "🇨🇳" },
  { name: "Japão", code: "JP", dial: "81", flag: "🇯🇵" },
  { name: "Coreia do Sul", code: "KR", dial: "82", flag: "🇰🇷" },
  { name: "Índia", code: "IN", dial: "91", flag: "🇮🇳" },
  { name: "Emirados Árabes Unidos", code: "AE", dial: "971", flag: "🇦🇪" },
  { name: "Arábia Saudita", code: "SA", dial: "966", flag: "🇸🇦" },
  { name: "Israel", code: "IL", dial: "972", flag: "🇮🇱" },
  { name: "Austrália", code: "AU", dial: "61", flag: "🇦🇺" },
  { name: "Nova Zelândia", code: "NZ", dial: "64", flag: "🇳🇿" },
];

const DEFAULT = COUNTRIES[0];

function parseValue(value: string): { country: Country; local: string } {
  const clean = (value || "").trim();
  if (clean.startsWith("+")) {
    const digits = clean.slice(1).replace(/\D/g, "");
    // find longest matching dial
    const sorted = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
    const match = sorted.find((c) => digits.startsWith(c.dial));
    if (match) return { country: match, local: digits.slice(match.dial.length) };
  }
  return { country: DEFAULT, local: clean.replace(/^\+?\d*/, "").replace(/\D/g, "") || clean.replace(/\D/g, "") };
}

interface PhoneInputProps {
  value: string;
  onChange: (fullValue: string) => void;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
}

export const PhoneInput = ({ value, onChange, placeholder, id, disabled, className }: PhoneInputProps) => {
  const parsed = useMemo(() => parseValue(value), [value]);
  const [country, setCountry] = useState<Country>(parsed.country);
  const [local, setLocal] = useState<string>(parsed.local);
  const [open, setOpen] = useState(false);

  const emit = (c: Country, l: string) => {
    const digits = l.replace(/\D/g, "");
    onChange(digits ? `+${c.dial} ${digits}` : "");
  };

  const handleSelect = (c: Country) => {
    setCountry(c);
    setOpen(false);
    emit(c, local);
  };

  const handleLocalChange = (v: string) => {
    // Allow only digits and spaces
    const cleaned = v.replace(/[^\d\s]/g, "");
    setLocal(cleaned);
    emit(country, cleaned);
  };

  return (
    <div className={cn("flex gap-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            disabled={disabled}
            className={cn("h-full gap-2 px-3 shrink-0 font-normal", className?.includes("h-8") && "h-8")}
          >
            <span className="text-lg leading-none">{country.flag}</span>
            <span className="text-sm">+{country.dial}</span>
            <ChevronsUpDown size={14} className="opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <Command
            filter={(itemValue, search) => {
              const s = search.toLowerCase();
              return itemValue.toLowerCase().includes(s) ? 1 : 0;
            }}
          >
            <CommandInput placeholder="Pesquisar país ou código..." />
            <CommandList>
              <CommandEmpty>Nenhum país encontrado.</CommandEmpty>
              <CommandGroup>
                {COUNTRIES.map((c) => (
                  <CommandItem
                    key={c.code}
                    value={`${c.name} ${c.dial} ${c.code}`}
                    onSelect={() => handleSelect(c)}
                    className="gap-2"
                  >
                    <span className="text-lg leading-none">{c.flag}</span>
                    <span className="flex-1 text-sm">{c.name}</span>
                    <span className="text-xs text-muted-foreground">+{c.dial}</span>
                    <Check size={14} className={cn("ml-1", country.code === c.code ? "opacity-100" : "opacity-0")} />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Input
        id={id}
        type="tel"
        inputMode="tel"
        value={local}
        onChange={(e) => handleLocalChange(e.target.value)}
        placeholder={placeholder || "9XX XXX XXX"}
        disabled={disabled}
        className={cn("flex-1", className?.includes("h-8") && "h-8")}
      />
    </div>
  );
};

export default PhoneInput;
